import React, { useMemo } from 'react';
import Select, { StylesConfig } from 'react-select';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../app/hooks';
import { getValueFromResource } from '../../common/timeLiveFunction';

interface GroupByProps {
    groupByLevels: (string | null)[];
    onChange: (levels: (string | null)[]) => void;
    scheduledTotal?: string | null;
    trackedTotal?: string | null;
}

interface GroupByOption {
    value: string | null;
    label: string;
}

const GROUP_BY_OPTIONS: Record<number, string[]> = {
    0: ['Project', 'User', 'Client', 'Month', 'Week', 'Date'],
    1: ['Project', 'Task', 'User', 'Client', 'Month', 'Week', 'Date'],
    2: ['Project', 'Task', 'User', 'Client', 'Month', 'Week', 'Date'],
};

const groupBySelectStyles: StylesConfig<GroupByOption, false> = {
    container: (base) => ({ ...base, minWidth: 150 }),
    control: (base) => ({ ...base, minHeight: 40, height: 40, background: '#fff', border: '1px solid #DDE1E6', borderRadius: 4, boxShadow: 'none', cursor: 'pointer' }),
    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    singleValue: (base) => ({ ...base, fontSize: 13, fontWeight: 500, color: '#212529' }),
    placeholder: (base) => ({ ...base, fontSize: 13, fontWeight: 500, color: '#212529' }),
    indicatorSeparator: () => ({ display: 'none' }),
    menu: (base) => ({ ...base, minWidth: 150, fontSize: 13, zIndex: 1200, boxShadow: '0 4px 14px rgba(0,0,0,0.10)', border: '1px solid #E9ECEF' }),
    option: (base, state) => ({ ...base, padding: '8px 12px', fontSize: 13, cursor: 'pointer', fontWeight: state.isSelected ? 600 : 400 }),
    menuPortal: (base) => ({ ...base, zIndex: 1200 }),
};

const ScheduleVSTrackingGroupBy: React.FC<GroupByProps> = ({ groupByLevels, onChange, scheduledTotal, trackedTotal }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const noneLabel = getValueFromResource('None', dispatch, t);
    const NONE_OPTION: GroupByOption = { value: null, label: noneLabel };

    const levels = useMemo<(string | null)[]>(() => [
        groupByLevels?.[0] ?? null,
        groupByLevels?.[1] ?? null,
        groupByLevels?.[2] ?? null,
    ], [groupByLevels]);

    const getOptionsForLevel = (levelIndex: number): GroupByOption[] => {
        let options: string[] = [];

        if (levelIndex === 0) {
            options = ['Project', 'User', 'Client', 'Month', 'Week', 'Date'];
        } else if (levelIndex === 1) {
            switch (levels[0]) {
                case 'Project':
                    options = ['Task', 'User', 'Month', 'Week', 'Date'];
                    break;
                case 'User':
                    options = ['Project', 'Task', 'Client', 'Month', 'Week', 'Date'];
                    break;
                case 'Client':
                    options = ['Project', 'Task', 'User', 'Month', 'Week', 'Date'];
                    break;
                case 'Month':
                    options = ['Project', 'Task', 'User', 'Client', 'Week', 'Date'];
                    break;
                case 'Week':
                    options = ['Project', 'Task', 'User', 'Client', 'Date'];
                    break;
                case 'Date':
                    options = ['Project', 'Task', 'User', 'Client'];
                    break;
                default:
                    options = [];
                    break;
            }
        } else if (levelIndex === 2) {
            switch (levels[0]) {
                case 'Project':
                    switch (levels[1]) {
                        case 'Task':
                            options = ['User', 'Month', 'Week', 'Date'];
                            break;
                        case 'User':
                            options = ['Task', 'Month', 'Week', 'Date'];
                            break;
                        case 'Month':
                            options = ['Task', 'User', 'Week', 'Date'];
                            break;
                        case 'Week':
                            options = ['Task', 'User', 'Date'];
                            break;
                        case 'Date':
                            options = ['Task', 'User'];
                            break;
                        default:
                            options = [];
                            break;
                    }
                    break;

                case 'User':
                    switch (levels[1]) {
                        case 'Project':
                            options = ['Task', 'Month', 'Week', 'Date'];
                            break;
                        case 'Task':
                            options = ['Month', 'Week', 'Date'];
                            break;
                        case 'Client':
                            options = ['Project', 'Task', 'Month', 'Week', 'Date'];
                            break;
                        case 'Month':
                            options = ['Project', 'Task', 'Client', 'Week', 'Date'];
                            break;
                        case 'Week':
                            options = ['Project', 'Task', 'Client', 'Date'];
                            break;
                        case 'Date':
                            options = ['Project', 'Task', 'Client'];
                            break;
                        default:
                            options = [];
                            break;
                    }
                    break;

                case 'Client':
                    switch (levels[1]) {
                        case 'Project':
                            options = ['Task', 'User', 'Month', 'Week', 'Date'];
                            break;
                        case 'Task':
                            options = ['User', 'Month', 'Week', 'Date'];
                            break;
                        case 'User':
                            options = ['Project', 'Task', 'Month', 'Week', 'Date'];
                            break;
                        case 'Month':
                            options = ['Project', 'Task', 'User', 'Week', 'Date'];
                            break;
                        case 'Week':
                            options = ['Project', 'Task', 'User', 'Date'];
                            break;
                        case 'Date':
                            options = ['Project', 'Task', 'User'];
                            break;
                        default:
                            options = [];
                            break;
                    }
                    break;

                case 'Month':
                    switch (levels[1]) {
                        case 'Project':
                            options = ['Task', 'User', 'Week', 'Date'];
                            break;
                        case 'Task':
                            options = ['User', 'Week', 'Date'];
                            break;
                        case 'User':
                            options = ['Project', 'Task', 'Client', 'Week', 'Date'];
                            break;
                        case 'Client':
                            options = ['Project', 'Task', 'User', 'Week', 'Date'];
                            break;
                        case 'Week':
                            options = ['Project', 'Task', 'User', 'Client', 'Date'];
                            break;
                        case 'Date':
                            options = ['Project', 'Task', 'User', 'Client'];
                            break;
                        default:
                            options = [];
                            break;
                    }
                    break;

                case 'Week':
                    switch (levels[1]) {
                        case 'Project':
                            options = ['Task', 'User', 'Date'];
                            break;
                        case 'Task':
                            options = ['User', 'Date'];
                            break;
                        case 'User':
                            options = ['Project', 'Task', 'Client', 'Date'];
                            break;
                        case 'Client':
                            options = ['Project', 'Task', 'User', 'Date'];
                            break;
                        case 'Date':
                            options = ['Project', 'Task', 'User', 'Client'];
                            break;
                        default:
                            options = [];
                            break;
                    }
                    break;

                case 'Date':
                    switch (levels[1]) {
                        case 'Project':
                            options = ['Task', 'User'];
                            break;
                        case 'Task':
                            options = ['User'];
                            break;
                        case 'User':
                            options = ['Project', 'Task', 'Client'];
                            break;
                        case 'Client':
                            options = ['Project', 'Task', 'User'];
                            break;
                        default:
                            options = [];
                            break;
                    }
                    break;

                default:
                    options = [];
                    break;
            }
        }

        return [NONE_OPTION, ...options.map(value => ({ value, label: value }))];
    };
    
    const handleLevelChange = (index: number, option: GroupByOption | null) => {
        const updated = [...levels];
        const newValue = option?.value ?? null;

        updated[index] = newValue;

        if (index === 0) {
            updated[1] = null;
            updated[2] = null;
        }

        if (index === 1) {
            updated[2] = null;
        }

        onChange(updated);
    };

    const showLevelTwo = levels[0] !== null;
    const showLevelThree = levels[0] !== null && levels[1] !== null;

    const getSelectedOption = (value: string | null, levelIndex: number): GroupByOption => {
        if (value === null) return NONE_OPTION;

        const exists = GROUP_BY_OPTIONS[levelIndex]?.includes(value);

        return exists ? { value, label: value } : NONE_OPTION;
    };

    return (
        <div className="d-flex align-items-center gap-2 px-6 py-2" style={{ background: '#F8F9FA', borderBottom: '1px solid #E9ECEF' }}>
            <span style={{ fontSize: 13, color: '#6B7280', marginRight: 4, flexShrink: 0 }}>
                {getValueFromResource('Group by', dispatch, t)}:
            </span>

            <Select<GroupByOption, false>
                value={getSelectedOption(levels[0], 0)}
                onChange={(option) => handleLevelChange(0, option)}
                options={getOptionsForLevel(0)}
                placeholder="Level 1"
                styles={groupBySelectStyles}
                isSearchable={false}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                classNamePrefix="group-by-select"
            />

            {showLevelTwo && (
                <Select<GroupByOption, false>
                    value={getSelectedOption(levels[1], 1)}
                    onChange={(option) => handleLevelChange(1, option)}
                    options={getOptionsForLevel(1)}
                    placeholder="Level 2"
                    styles={groupBySelectStyles}
                    isSearchable={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    classNamePrefix="group-by-select"
                />
            )}

            {showLevelThree && (
                <Select<GroupByOption, false>
                    value={getSelectedOption(levels[2], 2)}
                    onChange={(option) => handleLevelChange(2, option)}
                    options={getOptionsForLevel(2)}
                    placeholder="Level 3"
                    styles={groupBySelectStyles}
                    isSearchable={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    classNamePrefix="group-by-select"
                />
            )}

            {(scheduledTotal !== undefined || trackedTotal !== undefined) && (
                <div className="d-flex align-items-center gap-4" style={{ marginLeft: 12 }}>
                    {scheduledTotal !== undefined && (
                        <span style={{ fontSize: 13, color: '#6B7280' }}>
                            {getValueFromResource('Scheduled', dispatch, t)}:{' '}
                            <strong style={{ color: '#212529' }}>{scheduledTotal}</strong>
                        </span>
                    )}

                    {trackedTotal !== undefined && (
                        <span style={{ fontSize: 13, color: '#6B7280' }}>
                            {getValueFromResource('Tracked', dispatch, t)}:{' '}
                            <strong style={{ color: '#212529' }}>{trackedTotal}</strong>
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default ScheduleVSTrackingGroupBy;